import { useMutation } from '@tanstack/react-query';

export interface SasTokenResponse {
  blob_name: string;
  blob_path: string;
  blob_uri: string;
  sas_token: string;
  blob_endpoint: string;
}

export interface FileObject {
  file: File;
  blob_uri?: string;
  blob_endpoint?: string;
}

/**
 * This function gets the SAS token from the API to enable image upload to the blob and returns the blob endpoint
 * Regarding Files Sas token GET api endpoint, refer to {@link https://wcc-dev-apim-external.developer.azure-api.net/api-details#api=wcc-files-api&operation=get-token}
 */
export const getBlobEndpoint = async (): Promise<SasTokenResponse> => {
  const url = `${import.meta.env.VITE_APIM_BASE_URL}/files/v1/token`;
  try {
    const response = await fetch(url, {
      headers: {
        'Ocp-Apim-Subscription-Key': `${import.meta.env.VITE_APIM_API_KEY}`,
        Container: 'files',
      },
    });
    return await response.json();
  } catch (error) {
    throw new Error((error as Error).message);
  }
};

/**
 * This function uploads selected image (in File object) to the blob using the blob endpoint
 * @param selectedImage the image to be uploaded in File object
 * @returns file objects containing the uploaded image (File object), blob endpoint and blob uri
 */
export const uploadImageToBlob = async (selectedImage: File) => {
  try {
    const res = await getBlobEndpoint();
    const uploadImageUrl = res.blob_endpoint;
    const response = await fetch(uploadImageUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
      },
      body: selectedImage,
    });
    if (response.ok) {
      return {
        file: selectedImage,
        blob_endpoint: res.blob_endpoint,
        blob_uri: res.blob_uri,
      };
    } else {
      throw new Error('Failed to upload image to blob');
    }
  } catch (error) {
    throw new Error((error as Error).message);
  }
};

export const useUploadImage = () =>
  useMutation({
    mutationFn: async (selectedImage: File) =>
      await uploadImageToBlob(selectedImage),
  });
